/**
 * TERMINAL (EMV/SoftPOS) — the card-setup section on a SUNMI register's
 * terminal tab (owner-approved mockup board 6, 2026-10-14).
 * Spec: docs/agenticpos-rubytill/sunmi-card-setup.md §3, §5.
 *
 *   Device strip   — the catalog model, the Single/Dual variant switch, what the till reports
 *   PRIMARY slot   — the reader the Card button starts on (limited to the policy)
 *   BACKUP slot    — another allowed reader, or "No backup"
 *   Swap           — primary ↔ backup
 *   Switches       — backup behaviour (hidden with no backup)
 *
 * Saves itself: `PUT …/payment-types { cardSetup }` sent alone — never joins
 * the Payment Types save bar. Only mounted when the register's catalog model
 * carries a `cardSetupPolicy` (the caller gates on it).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpDown, Check, CreditCard, Nfc, Plus, Smartphone, KeyRound } from 'lucide-react';
import { api } from '../../../services/apiService';
import { hardwareApi, type DeviceModelRow } from '../../../services/hardwareApi';
import { toastService } from '../../../services/toastService';
import { RubyGemMark } from '../../RubyGemMark';
import { RubyLoader } from '../../RubyLoader';
import { Switch, Pill } from '../payment-types/ui';
import { explainPaymentError } from '../payment-types/errors';
import { RegisterDeviceModal } from './RegisterDeviceModal';
import { DeviceTable } from './DeviceTable';
import { MagtekLocationCard } from '../unigate/MagtekLocationCard';
import { SunbayLocationCard } from '../sunbay/SunbayLocationCard';
import { registrationChoices, type RegistrationFlowId } from './registrationFlows';
import { ReaderPicker, Segmented, type PickOption } from './ReaderPicker';
import { modelClaims } from './useReportedModel';

/** Picker ids that open the registration form instead of choosing a reader. */
const REGISTER_PREFIX = '__register_';
import {
  type CardSetup, type CardSetupPolicy, type Role, type CatalogCaps,
  readSetup, defaultSetup, setRole, swapRoles, canSwap, setupProblems, sameSetup, readerId,
  devicesForRole, listedKinds, allowedKinds, readerKindOf, kindLabel, capsFor, capabilitySummary,
  policyOf, masked, KIND_LABEL, autoFill, otherLocationReaders, type TipLocation,
} from './cardSetupModel';

const RUBY_UNDERLINE = { background: 'linear-gradient(90deg, #e8365a, #DC143C, #9F1239)', height: '3px' };

interface Props {
  tenantId: string;
  terminal: { terminalId: string; locationId: string; locationName?: string; registerName: string };
  model: DeviceModelRow;
  policy: CardSetupPolicy;
  /** What the till reports (`hardwareModel`); undefined while reading, null when none. */
  reported?: string | null;
  /** The app build the till reports (e.g. android_dual_screen). Known → the variant follows it, no switch. */
  reportedBuildTarget?: string | null;
  /** @deprecated The section registers devices itself now ("Register payment device"). */
  onRegisterReader?: () => void;
  /** A tip location picked on Payment Types before any readers were saved — used on first setup. */
  pendingTipLocation?: TipLocation | null;
  /** Bumped by the caller after a device registers, to re-read the pool. */
  refreshKey?: number;
  /** After a variant switch — the caller re-reads the terminal / model. */
  onModelChanged?: (modelId: string) => void;
  onSaved?: () => void;
}

/**
 * Is `m` the same MACHINE as `current`? The card-setup family ('sunmi_t3') spans
 * the T3 80mm and the T3 Pro, so the Single/Dual switch must also match the
 * hardware profile — a T3 Pro register is never offered a T3 80mm row. A row
 * without an install profile on either side falls back to family-only, as before.
 */
export function sameMachine(m: DeviceModelRow, current: DeviceModelRow): boolean {
  const a = (m as any)?.appProfile?.installProfileId;
  const b = (current as any)?.appProfile?.installProfileId;
  return !a || !b || a === b;
}

const transportOf = (d: any) => {
  const cls = String(d?.deviceClass || '');
  if (cls === 'dejavoo_spin_cloud') return 'SPIn Cloud';
  if (cls === 'dejavoo_spin_usb') return 'USB';
  if (cls === 'dejavoo_rubytill_pay') return 'RubyTill Pay';
  return cls || 'unknown';
};

const deviceSub = (d: any) => [
  transportOf(d),
  d?.tpn || d?.spinTpn ? `TPN ${masked(d.tpn || d.spinTpn)}` : null,
  d?.serialNumber ? `serial ••${String(d.serialNumber).slice(-4)}` : null,
].filter(Boolean).join(' · ');

export const CardSetupTerminalSection: React.FC<Props> = ({
  tenantId, terminal, model, policy, reported, reportedBuildTarget, refreshKey = 0, onModelChanged, onSaved, pendingTipLocation,
}) => {
  const registerId = terminal.terminalId;
  const [devices, setDevices] = useState<any[]>([]);
  const [catalog, setCatalog] = useState<CatalogCaps>({});
  const [variants, setVariants] = useState<DeviceModelRow[]>([]);
  const [base, setBase] = useState<CardSetup | null>(null);
  const [draft, setDraft] = useState<CardSetup | null>(null);
  const [stored, setStored] = useState(false);
  const [status, setStatus] = useState<Record<string, any>>({});
  const onRtpayStatus = useCallback((id: string) => (r: any) => setStatus((s) => ({ ...s, [id]: r })), []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** "Register payment device": closed → chooser → a flow. */
  const [registering, setRegistering] = useState<false | 'choose' | RegistrationFlowId>(false);
  /** Registered from a slot's picker → the new reader goes into THAT slot. */
  const registerRole = React.useRef<Role | null>(null);
  /** The device being edited from the list. */
  const [editing, setEditing] = useState<any | null>(null);
  const [poolKey, setPoolKey] = useState(0);
  /** Unassigned readers at the tenant's OTHER locations (listed, not selectable — no move route). */
  const [elsewhere, setElsewhere] = useState<Array<{ device: any; locationName: string }>>([]);
  /** Ids in the pool before a registration — the new one is whatever appears after. */
  const knownIds = React.useRef<Set<string> | null>(null);
  /** A Tap to Pay create returns its id directly. */
  const createdId = React.useRef<string | null>(null);

  /* Every edit here saves itself — a reader picked (or just registered into a
     slot) reaches the till without a second "Save" step to forget. */
  const autoSave = React.useRef(false);
  /** Save even though the draft equals the (unsaved) suggestion — see the registration block in load(). */
  const forceSave = React.useRef(false);
  const saveRef = React.useRef<(() => Promise<void>) | null>(null);
  const edit = useCallback((next: CardSetup) => { autoSave.current = true; setDraft(next); }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [pt, pool, pay, terms] = await Promise.all([
        api.posNmi.getPaymentTypes(tenantId, registerId).catch(() => null),
        api.posNmi.listEmvDevicesAtLocation(tenantId, terminal.locationId).catch(() => null),
        hardwareApi.listModels({ category: 'payment_device' }).catch(() => null),
        hardwareApi.listModels({ category: 'pos_terminal' }).catch(() => null),
      ]);
      const list: any[] = Array.isArray(pool) ? pool : (pool?.devices || pool?.items || []);
      const cat: CatalogCaps = Object.fromEntries((pay?.items || []).map((m) => [m.modelId, m as any]));
      setDevices(list);
      setCatalog(cat);
      setVariants((terms?.items || []).filter((m) => policyOf(m)?.profile === policy.profile && sameMachine(m, model)));
      const s = readSetup(pt?.cardSetup, policy);
      setStored(!!s);
      // A pool re-read (a device was just added) keeps unsaved edits.
      const editing = !!baseRef.current && !!draftRef.current && !sameSetup(baseRef.current, draftRef.current);
      let current = draftRef.current;
      if (!editing) {
        // Smart defaults on first setup (spec §3) — shown as a suggestion until saved.
        let next = s || defaultSetup(policy, list, registerId, cat);
        if (!s && pendingTipLocation) next = { ...next, tipLocation: pendingTipLocation };
        setBase(next); setDraft(next);
        current = next;
      }
      // A device was just registered: drop it into the empty slot (unsaved).
      if (current && (knownIds.current || createdId.current)) {
        const fresh = list.filter((d) => d.deviceId === createdId.current || (knownIds.current && !knownIds.current.has(d.deviceId)));
        let filled = current;
        const role = registerRole.current;
        for (const d of fresh) filled = role && allowedKinds(policy, role).includes(readerKindOf(d) as any) ? setRole(filled, role, d.deviceId) : autoFill(filled, policy, d);
        if (filled !== current) edit(filled);
        // Not a Tap to Pay phone: it still has to be PAIRED, so the owner saves after pairing.
        else if (!s && fresh.length && fresh.every((d) => String(d.deviceClass) !== 'dejavoo_rubytill_pay')) {
          /* The new device was ALREADY the suggested reader (e.g. the only CPad
             at the location), so nothing changed — and nothing was ever saved:
             the page read "Primary" while the till had no card setup at all.
             Registering is the deliberate act; it saves the setup it completes. */
          forceSave.current = true; autoSave.current = true; setDraft({ ...filled });
        }
        knownIds.current = null; createdId.current = null; registerRole.current = null;
      }
    } catch (e: any) {
      setError(e?.message || 'Could not read the card readers.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, registerId, terminal.locationId, policy, pendingTipLocation]);

  const draftRef = React.useRef<CardSetup | null>(null);
  const baseRef = React.useRef<CardSetup | null>(null);
  draftRef.current = draft;
  baseRef.current = base;

  useEffect(() => { load(); }, [load, refreshKey, poolKey]);

  /* Other locations — no route moves a device between locations, so these are
     shown (disabled, "Registered at …") only so the owner can find them. */
  useEffect(() => {
    let live = true;
    (async () => {
      const req: any = await api.locationBilling.listTenantRequests(tenantId).catch(() => null);
      const others = (req?.requests || [])
        .filter((r: any) => r.status === 'active' && r.locationId && r.locationId !== terminal.locationId)
        .map((r: any) => ({ locationId: r.locationId as string, name: (r.name || r.locationId) as string }));
      const lists = await Promise.all(others.map((l: { locationId: string; name: string }) =>
        api.posNmi.listEmvDevicesAtLocation(tenantId, l.locationId)
          .then((p: any) => ({ l, list: (Array.isArray(p) ? p : (p?.devices || p?.items || [])) as any[] }))
          .catch(() => ({ l, list: [] as any[] }))));
      if (!live) return;
      setElsewhere(lists.flatMap(({ l, list }) => otherLocationReaders(list, policy).map((device) => ({ device, locationName: l.name }))));
    })();
    return () => { live = false; };
  }, [tenantId, terminal.locationId, policy, poolKey]);


  const byId = useCallback((id: string | null) => devices.find((d) => d.deviceId === id) || null, [devices]);

  // Live status of every RubyTill Pay device at the location (paired / online / app version).
  const rtpayIds = useMemo(() => devices
    .filter((d) => d && String(d.deviceClass) === 'dejavoo_rubytill_pay')
    .map((d) => d.deviceId as string), [devices]);
  useEffect(() => {
    let live = true;
    for (const id of rtpayIds) {
      if (status[id]) continue;
      api.posNmi.getRtpay(tenantId, id).then((r: any) => { if (live) setStatus((s) => ({ ...s, [id]: r })); }).catch(() => {});
    }
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, rtpayIds.join(',')]);

  useEffect(() => {
    if (!autoSave.current || !draft || saving || switching) return;
    autoSave.current = false;
    if (setupProblems(draft, policy, devices, catalog).length > 0) return; // the alert line says why
    const force = forceSave.current;
    forceSave.current = false;
    if (!force && base && sameSetup(base, draft)) return;
    saveRef.current?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  if (loading || !draft) return <RubyLoader variant="section" className="py-10" />;

  const dirty = !sameSetup(base, draft);
  const problems = setupProblems(draft, policy, devices, catalog);
  const busy = saving || switching;
  const primaryId = readerId(draft, 'primary');
  const backupId = readerId(draft, 'backup');
  const hasBackup = !!backupId;

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const res = await api.posNmi.putPaymentTypes(tenantId, registerId, { cardSetup: draft });
      if (res?.error) throw Object.assign(new Error(res.message || res.error), { code: res.error, serverMessage: res.message });
      setBase(draft); setStored(true);
      toastService.success('Card readers saved and sent to the till.');
      onSaved?.();
    } catch (e: any) {
      setError(explainPaymentError(e, 'Card readers were not saved.'));
    } finally {
      setSaving(false);
    }
  };
  saveRef.current = save;

  const switchVariant = async (modelId: string) => {
    if (modelId === model.modelId) return;
    setSwitching(true); setError(null);
    try {
      await api.posNmi.setTerminalDeviceModel(tenantId, registerId, modelId);
      toastService.success('Terminal model changed.');
      onModelChanged?.(modelId);
    } catch (e: any) {
      setError(e?.serverMessage || e?.message || 'The model was not changed.');
    } finally {
      setSwitching(false);
    }
  };

  const optionsFor = (role: Role): PickOption[] => {
    const fit = devicesForRole(devices, policy, role, registerId);
    const other = readerId(draft, role === 'primary' ? 'backup' : 'primary');
    const opts: PickOption[] = fit.map((d) => ({
      id: d.deviceId,
      label: `${kindLabel(readerKindOf(d))} · ${d.deviceName || 'Card reader'}`,
      sub: `${deviceSub(d)} · ${capabilitySummary(capsFor(d, catalog))}`,
      disabled: d.deviceId === other,
      tag: d.deviceId === other ? (role === 'primary' ? 'Backup' : 'Primary') : undefined,
    }));
    const kinds = allowedKinds(policy, role);
    for (const e of elsewhere.filter((x) => kinds.includes(readerKindOf(x.device) as any))) {
      opts.push({
        id: `__elsewhere_${e.device.deviceId}`, group: 'Other locations', disabled: true,
        label: `${kindLabel(readerKindOf(e.device))} · ${e.device.deviceName || 'Card reader'}`,
        sub: `Registered at ${e.locationName}`,
      });
    }
    // A reader that isn't registered yet can't be picked — offer to register it from here.
    for (const c of choices.filter((x) => x.enabled && x.kinds.some((k) => kinds.includes(k)))) {
      opts.push({ id: `${REGISTER_PREFIX}${c.id}`, group: 'Register new', label: `Register ${c.title}`, sub: 'Opens the form, then fills this slot' });
    }
    for (const r of listedKinds(policy, role).filter((x) => !x.enabled)) {
      opts.push({ id: `__next_${r.kind}`, label: KIND_LABEL[r.kind], sub: 'Not available yet', disabled: true, tag: 'Coming next', group: 'Coming next' });
    }
    return opts;
  };

  const openRegister = (flow: RegistrationFlowId | null, role: Role | null) => {
    knownIds.current = new Set(devices.map((d) => d.deviceId));
    registerRole.current = role;
    setRegistering(flow || 'choose');
  };

  const allowedText = (role: Role) => {
    const kinds = allowedKinds(policy, role).map((k) => KIND_LABEL[k]);
    if (role === 'backup' && policy.allowNoBackup) kinds.push('No backup');
    return kinds.join(' · ') || 'nothing';
  };

  const statusPill = (d: any) => {
    if (!d) return null;
    if (String(d.deviceClass) === 'dejavoo_rubytill_pay') {
      const st = status[d.deviceId]?.status;
      if (!status[d.deviceId]) return <Pill>Checking…</Pill>;
      return <Pill tone={st?.online ? 'ok' : st?.paired ? 'warn' : 'off'}>{st?.online ? 'Online' : st?.paired ? 'Paired · offline' : 'Not paired'}</Pill>;
    }
    const s = String(d.connectionStatus || '').toLowerCase();
    if (d.connected === false || s === 'disconnected' || s === 'offline') return <Pill tone="off">Offline</Pill>;
    if (d.connected === true || s === 'connected' || s === 'online') return <Pill tone="ok">Online</Pill>;
    return null;
  };

  const slot = (role: Role) => {
    const id = role === 'primary' ? primaryId : backupId;
    const d = byId(id);
    const kind = d ? readerKindOf(d) : null;
    const isTap = kind === 'tap_to_pay';
    const isRtpay = !!d && String(d.deviceClass) === 'dejavoo_rubytill_pay';
    const isCpad = isRtpay && String(d?.rtpay?.cardEngine ?? d?.rtpayCardEngine ?? '') === 'sunbay';
    const st = d ? status[d.deviceId] : null;
    const primary = role === 'primary';
    return (
      <div data-testid={`cs-slot-${role}`}
        className={`rounded-xl border-2 p-4 ${primary ? 'border-ruby bg-[#FFF1F3]/40' : 'border-stone-200 bg-white'}`}>
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className={`text-[11px] font-bold uppercase tracking-wide ${primary ? 'text-ruby-deep' : 'text-stone-500'}`}>
            {primary ? 'Primary · default on card' : 'Backup'}
          </span>
          {statusPill(d)}
        </div>
        <div className="mb-3 flex items-start gap-3">
          <div className={`grid h-[34px] w-[34px] shrink-0 place-items-center rounded-[10px] ${primary ? 'bg-white text-ruby' : 'bg-stone-100 text-stone-600'}`}>
            {isTap ? <Nfc size={18} /> : <CreditCard size={18} />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[14px] font-semibold text-stone-900">{d ? (isCpad ? 'RubyTill Pay CPad' : kindLabel(kind)) : (primary ? 'No reader picked' : 'No backup')}</div>
            <div className="text-xs text-stone-600">
              {d
                ? <>{d.deviceName || 'Card reader'} · {deviceSub(d)} · {capabilitySummary(capsFor(d, catalog))}</>
                : primary ? 'The Card button needs a primary reader.' : 'The till uses only the primary reader.'}
            </div>
            {isTap && d && (
              <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-stone-600">
                {st?.rtpay?.merchantCodeLast4 && <span>Merchant code ••••{st.rtpay.merchantCodeLast4}</span>}
                {st?.status?.appVersion && <span>· app v{st.status.appVersion}</span>}
              </div>
            )}
          </div>
        </div>
        <ReaderPicker
          ariaLabel={primary ? 'Primary reader' : 'Backup reader'}
          value={id}
          placeholder={primary ? 'Pick the primary reader' : 'Pick a backup reader'}
          noneLabel={!primary && policy.allowNoBackup ? 'No backup' : undefined}
          options={optionsFor(role)}
          onChange={(v) => (v?.startsWith(REGISTER_PREFIX)
            ? openRegister(v.slice(REGISTER_PREFIX.length) as RegistrationFlowId, role)
            : edit(setRole(draft, role, v)))}
          disabled={busy}
        />
        {isRtpay && d && <RtpayPairing tenantId={tenantId} deviceId={d.deviceId} status={st ?? null} disabled={busy} onStatus={onRtpayStatus(d.deviceId)} />}
        <p className="mt-2 text-[11.5px] text-stone-500">Allowed here: {allowedText(role)}</p>
      </div>
    );
  };

  const choices = registrationChoices(policy);
  const variantOptions = variants
    .map((v) => ({ id: v.modelId, label: policyOf(v)?.variant === 'single' ? 'Single screen' : 'Dual screen', order: policyOf(v)?.variant === 'single' ? 0 : 1 }))
    .sort((a, b) => a.order - b.order);
  const tillMatches = modelClaims(model, reported);
  /* The till says which build it runs, so the variant is a FACT, not a choice.
     The switch is only the fallback for a till that hasn't reported yet. */
  const reportedVariant = tillMatches && reportedBuildTarget
    ? variants.find((v) => (v.buildTarget || '') === reportedBuildTarget) || null
    : null;
  const variantLabel = (v: DeviceModelRow) => policyOf(v)?.variant === 'single' ? 'Single screen' : 'Dual screen';

  const behaviour: Array<{ key: 'showBackupAlways' | 'offerBackupOnFail' | 'startOnBackupWhenPrimaryOffline'; title: string; desc?: string }> = [
    { key: 'showBackupAlways', title: 'Show "Use backup reader" on every card payment' },
    { key: 'offerBackupOnFail', title: 'Offer the backup when the primary fails', desc: 'Only after the till knows nothing was charged.' },
    { key: 'startOnBackupWhenPrimaryOffline', title: 'Start on the backup when the primary is offline' },
  ];

  return (
    <section data-testid="card-setup-terminal" className="mb-4 overflow-hidden rounded-2xl border border-stone-900 bg-white">
      <header className="bg-black px-[18px] py-4 text-white">
        <h3 className="m-0 text-[15px] font-semibold">Card readers</h3>
        <div className="mt-0.5 text-xs text-stone-400">The Card button starts on the primary; the backup is one press away.</div>
      </header>
      <div style={RUBY_UNDERLINE} />

      {/* Device strip */}
      <div data-testid="cs-device-strip" className="flex flex-wrap items-center gap-3 border-b border-stone-200 px-[18px] py-3">
        <div className="grid h-[34px] w-[34px] place-items-center rounded-[10px] bg-stone-100 text-stone-700"><Smartphone size={18} /></div>
        <div className="min-w-0">
          <div className="text-[14px] font-semibold text-stone-900">{model.manufacturer && !model.modelName.startsWith(model.manufacturer) ? `${model.manufacturer} ` : ''}{model.modelName}</div>
          <div className="text-xs text-stone-500">{policy.customerDisplay ? 'Customer display on the till' : 'No customer display'}</div>
        </div>
        {!reportedVariant && variantOptions.length > 1 && (
          <Segmented ariaLabel="Screen variant" value={model.modelId} options={variantOptions} onChange={switchVariant} disabled={busy} />
        )}
        {switching && <RubyGemMark size={16} drawing />}
        {reportedVariant && reportedVariant.modelId !== model.modelId ? (
          <span data-testid="cs-till-reports-other" className="inline-flex items-center gap-2 rounded-full bg-amber-50 py-1 pl-2.5 pr-1 text-[11px] font-semibold text-amber-800">
            Till runs {variantLabel(reportedVariant)}
            <button type="button" disabled={busy} onClick={() => switchVariant(reportedVariant.modelId)}
              className="min-h-[28px] rounded-full bg-black px-2.5 text-[11px] font-semibold text-white">Use {variantLabel(reportedVariant)}</button>
          </span>
        ) : tillMatches && reported && (
          <span data-testid="cs-till-reports" className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-[11px] font-semibold text-green-700">
            <Check size={12} /> Till reports {reported}{reportedVariant ? ` · ${variantLabel(reportedVariant)}` : ''}
          </span>
        )}
      </div>

      <div className="grid gap-3 p-[18px] md:grid-cols-2">
        {slot('primary')}
        {slot('backup')}
      </div>

      <div className="flex flex-wrap items-center gap-2 px-[18px] pb-3">
        <button type="button" data-testid="cs-swap" disabled={busy || !canSwap(draft, policy, devices)}
          onClick={() => edit(swapRoles(draft))}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-stone-300 bg-white px-4 text-sm font-medium text-stone-800 hover:bg-stone-50 disabled:opacity-40">
          <ArrowUpDown size={15} /> Swap primary and backup
        </button>
        <button type="button" data-testid="cs-register-device" disabled={busy}
          onClick={() => openRegister(null, null)}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-lg px-3 text-sm font-medium text-ruby hover:bg-[#FFF1F3] disabled:opacity-40">
          <Plus size={15} /> Register payment device
        </button>
      </div>

      {registering && (
        <RegisterDeviceModal
          tenantId={tenantId}
          location={{ locationId: terminal.locationId, name: terminal.locationName || 'This location' }}
          choices={choices}
          initialFlow={registering === 'choose' ? null : registering}
          onClose={() => { knownIds.current = null; registerRole.current = null; setRegistering(false); }}
          onRegistered={(id) => { createdId.current = id; setPoolKey((k) => k + 1); }}
        />
      )}

      {editing && (
        <RegisterDeviceModal
          tenantId={tenantId}
          location={{ locationId: terminal.locationId, name: terminal.locationName || 'This location' }}
          choices={choices}
          edit={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setStatus((x) => { const n = { ...x }; delete n[editing.deviceId]; return n; }); setPoolKey((k) => k + 1); }}
        />
      )}

      {hasBackup && (
        <div className="border-t border-stone-200" data-testid="cs-switches">
          {behaviour.map((b) => (
            <div key={b.key} className="flex min-h-[56px] items-center justify-between gap-3 border-b border-stone-100 px-[18px] py-2 last:border-b-0">
              <div className="min-w-0">
                <div className="text-[13.5px] text-stone-800">{b.title}</div>
                {b.desc && <div className="text-xs text-stone-500">{b.desc}</div>}
              </div>
              <Switch on={draft[b.key]} label={b.title} disabled={busy} onChange={(v) => edit({ ...draft, [b.key]: v })} />
            </div>
          ))}
        </div>
      )}

      <DeviceTable
        registerId={registerId}
        devices={devices.filter((d) => !!readerKindOf(d))}
        draft={draft}
        policy={policy}
        status={status}
        disabled={busy}
        onEdit={setEditing}
        onUse={(role, id) => edit(setRole(draft, role, id))}
      />

      {/* MagTek readers authorise with UniGate credentials — platform default or this location's own. */}
      {policy.readers.some((r) => (r.kind === 'magtek_ped' || r.kind === 'magtek_go') && r.enabled) && (
        <div className="border-t border-stone-200 px-[18px] py-3">
          <MagtekLocationCard tenantId={tenantId} locationId={terminal.locationId} locationName={terminal.locationName || 'This location'} />
        </div>
      )}

      {/* SUNBAY readers authorise with this location's merchant id (platform credentials are on Payment processors). */}
      {policy.readers.some((r) => r.kind === 'sunbay_tapro' && r.enabled) && (
        <div className="border-t border-stone-200 px-[18px] py-3">
          <SunbayLocationCard tenantId={tenantId} locationId={terminal.locationId} locationName={terminal.locationName || 'This location'} />
        </div>
      )}

      {(error || (dirty || !stored) && problems.length > 0) && (
        <div role="alert" className="border-t border-stone-200 px-[18px] py-3 text-[12.5px] text-ruby-deep">
          {error || problems[0]}
        </div>
      )}

      <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-stone-200 bg-stone-50 px-[18px] py-3">
        {!stored && !dirty && (
          <span data-testid="cs-not-saved" className="mr-auto rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
            Not saved — the till is not using these readers yet.
          </span>
        )}
        {dirty && (
          <button type="button" disabled={busy} onClick={() => setDraft(base)}
            className="min-h-[44px] rounded-lg px-4 text-sm font-medium text-stone-600 hover:bg-stone-100 disabled:opacity-50">
            Discard
          </button>
        )}
        <button type="button" disabled={busy || (!dirty && stored) || problems.length > 0} onClick={save}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-black px-5 text-sm font-semibold text-white hover:bg-stone-800 disabled:opacity-40">
          {saving ? <RubyGemMark size={18} drawing color="currentColor" /> : <Check size={16} />}
          Save card readers
        </button>
      </footer>
    </section>
  );
};

/**
 * RubyTill Pay pairing, on the register — the terminal pairs with THIS register's reader.
 *
 * Unpaired: a code is issued as soon as the reader is picked (once, never in a loop), shown
 * large with Copy and a countdown, and the panel watches the device until it pairs, then the
 * code is dropped. Paired: status only; "Pair a new device" asks first, because a new pairing
 * signs the current terminal out (pos-pay-devices bumps the token version on every pair).
 *
 * Security: the code lives only in this component's memory (never logged, never stored),
 * is single use and expires in 10 minutes server side; issuing needs a super-admin token and
 * is audited by super-admin-ext3. An expired code is never replaced automatically.
 */
const RtpayPairing: React.FC<{
  tenantId: string; deviceId: string; status: any; disabled?: boolean;
  onStatus: (r: any) => void;
}> = ({ tenantId, deviceId, status, disabled, onStatus }) => {
  const paired = !!status?.status?.paired;
  const online = !!status?.status?.online;
  const appVersion: string | undefined = status?.status?.appVersion;
  const [code, setCode] = useState<{ code: string; expiresAt: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmNew, setConfirmNew] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [autoTried, setAutoTried] = useState(false);
  const onStatusRef = useRef(onStatus);
  onStatusRef.current = onStatus;

  const issue = useCallback(async () => {
    setBusy(true); setErr(null); setConfirmNew(false);
    try {
      const r = await api.posNmi.newRtpayPairingCode(tenantId, deviceId);
      const exp = typeof r.expiresAt === 'number' ? r.expiresAt : Date.parse(String(r.expiresAt));
      setCode({ code: String(r.code), expiresAt: Number.isFinite(exp) ? exp : Date.now() + 10 * 60_000 });
    } catch (e: any) { setErr(e?.serverMessage || e?.message || 'No code came back.'); }
    finally { setBusy(false); }
  }, [tenantId, deviceId]);

  // Auto-issue ONCE when an unpaired terminal is picked; status must be known first.
  useEffect(() => {
    if (disabled || autoTried || status == null || paired) return;
    setAutoTried(true);
    void issue();
  }, [disabled, autoTried, status, paired, issue]);

  // A new device / reader resets everything (and forgets any code on screen).
  useEffect(() => { setCode(null); setAutoTried(false); setConfirmNew(false); setErr(null); }, [deviceId]);

  // While a code is live: tick the countdown and watch for the pairing to land.
  useEffect(() => {
    if (!code) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    const p = setInterval(() => {
      api.posNmi.getRtpay(tenantId, deviceId).then((r: any) => {
        onStatusRef.current(r);
        const at = Date.parse(String(r?.rtpay?.pairedAt || ''));
        if (r?.status?.paired && Number.isFinite(at) && at > code.expiresAt - 10 * 60_000) setCode(null);
      }).catch(() => {});
    }, 3000);
    return () => { clearInterval(t); clearInterval(p); };
  }, [code, tenantId, deviceId]);

  const left = code ? Math.max(0, Math.round((code.expiresAt - now) / 1000)) : 0;
  const expired = !!code && left === 0;
  const copy = async () => {
    if (!code) return;
    try { await navigator.clipboard.writeText(code.code); toastService.success('Pairing code copied'); }
    catch { toastService.error('Could not copy — read the code from the screen.'); }
  };
  const btn = 'inline-flex min-h-[44px] items-center gap-2 rounded-lg px-3 text-sm font-medium disabled:opacity-40';

  return (
    <div className="mt-3 rounded-xl border border-stone-200 bg-white p-3" data-testid="cs-pairing">
      {code && !expired ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-lg bg-stone-900 px-4 py-2 text-2xl font-bold tracking-[0.35em] text-white tabular-nums" data-testid="cs-pairing-code">{code.code}</span>
          <button type="button" onClick={copy} className={`${btn} border border-stone-300 bg-white text-stone-800 hover:bg-stone-50`}>Copy</button>
          <div className="min-w-[180px] flex-1 text-xs text-stone-600">
            <div className="font-semibold text-stone-800">Type this on the CPad Pay's RubyTill Pay "Pair this terminal" screen</div>
            <div className="tabular-nums">Single use · expires in {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')} · waiting for the terminal…</div>
          </div>
        </div>
      ) : paired && !confirmNew ? (
        <div className="flex flex-wrap items-center gap-3">
          <Pill tone={online ? 'ok' : 'warn'}>{online ? 'Paired · online' : 'Paired · offline'}</Pill>
          {appVersion && <span className="text-xs text-stone-600">RubyTill Pay v{appVersion}</span>}
          <button type="button" disabled={disabled || busy} onClick={() => setConfirmNew(true)} className={`${btn} ml-auto border border-stone-300 bg-white text-stone-800 hover:bg-stone-50`}>
            <KeyRound size={15} /> Pair a new device
          </button>
        </div>
      ) : paired && confirmNew ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex-1 text-sm text-stone-800">A new pairing signs out the terminal paired now. Continue?</span>
          <button type="button" onClick={() => setConfirmNew(false)} className={`${btn} border border-stone-300 bg-white text-stone-800`}>Cancel</button>
          <button type="button" disabled={busy} onClick={issue} className={`${btn} bg-black text-white hover:bg-stone-800`}>
            {busy ? <RubyGemMark size={15} drawing color="currentColor" /> : <KeyRound size={15} />} Get pairing code
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Pill tone="off">{expired ? 'Code expired' : 'Not paired'}</Pill>
          <button type="button" disabled={disabled || busy} onClick={issue} className={`${btn} bg-black text-white hover:bg-stone-800`}>
            {busy ? <RubyGemMark size={15} drawing color="currentColor" /> : <KeyRound size={15} />} {expired ? 'New pairing code' : 'Get pairing code'}
          </button>
        </div>
      )}
      {err && <div className="mt-2 text-xs text-ruby-deep">{err}</div>}
    </div>
  );
};

export default CardSetupTerminalSection;
